export const props = Object.freeze({
  /**
   * Override the row's cross-axis alignment for this single column.
   *
   * @type {String}
   * @storybook-control select
   * @default undefined
   */
  alignSelf: {
    type: String,
    default: undefined,
    validator: (value) => ["auto", "start", "end", "center", "baseline", "stretch"].includes(value),
  },
  /**
   * Cross-axis alignment of the column's children.
   *
   * @type {String}
   * @storybook-control select
   * @default undefined
   */
  alignItems: {
    type: String,
    required: false,
    validator: (value) => ["start", "center", "end", "stretch"].includes(value),
  },
  /**
   * Column span out of 12 at the default breakpoint. Omit to let the column flex to fill available
   * space.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  cols: {
    type: [String, Number],
    required: false,
  },
  /**
   * Column span override below the small breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  colsSm: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Column span override at the medium breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  colsMd: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Column span override at the large breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  colsLg: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Flex direction applied to the column's children.
   *
   * @type {String}
   * @storybook-control select
   * @default undefined
   */
  flexDirection: {
    type: String,
    required: false,
    validator: (value) => ["row", "row-reverse", "column", "column-reverse"].includes(value),
  },
  /**
   * Drop the leading-edge gutter on this column. Useful for the first column when it should align
   * with the parent edge.
   *
   * @type {Boolean}
   * @default false
   */
  noStartGap: {
    type: Boolean,
    default: false,
  },
  /**
   * Drop the trailing-edge gutter on this column. Useful for the last column when it should align
   * with the parent edge.
   *
   * @type {Boolean}
   * @default false
   */
  noEndGap: {
    type: Boolean,
    default: false,
  },
  /**
   * Column span at the large breakpoint.
   *
   * @deprecated Superseded by colsLg.
   * @type {String|Number}
   * @storybook-category Deprecated
   * @default undefined
   */
  lg: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Column span at the medium breakpoint.
   *
   * @deprecated Superseded by colsMd.
   * @type {String|Number}
   * @storybook-category Deprecated
   * @default undefined
   */
  md: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Column span below the small breakpoint.
   *
   * @deprecated Superseded by colsSm.
   * @type {String|Number}
   * @storybook-category Deprecated
   * @default undefined
   */
  sm: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Leading-edge margin in column units. 1–11 push the column to the right by N/12 of the row
   * width; 0 leaves it in place.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  offset: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Offset override at the large breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  offsetLg: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Offset override at the medium breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  offsetMd: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Offset override below the small breakpoint.
   *
   * @type {String|Number}
   * @storybook-control number
   * @default undefined
   */
  offsetSm: {
    type: [String, Number],
    default: undefined,
  },
});
